import React, { useRef, useState, useEffect, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import { Card } from 'react-bootstrap';
import BucketRangeButtons from './BucketRangeButtons';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  GridComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import dayjs from 'dayjs';
import axios from 'axios';
import { FallingLines } from 'react-loader-spinner';
import { useAppContext } from '../../../providers/AppProvider';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  ToolboxComponent,
  GridComponent,
  LineChart,
  CanvasRenderer
]);

const MachineAmpsGraph = ({
  bucket,
  dynamicBucket = false,
  machineName,
  fields,
  startTime,
  endTime,
  title
}) => {
  const [machineCurrent, setMachineCurrent] = useState([]);
  const [loading, setLoading] = useState(true); // Loading state
  const [currentBucket, setCurrentBucket] = useState(bucket); // Track the current bucket
  const chartRef = useRef(null);

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true); // Set loading to true before fetching
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${currentBucket}`
        // {
        //   params: { status: 'running' }
        // }
      );
      const keys = Object.keys(response.data)[0];
      const data = response.data[keys];

      // Sort the data by timestamp to ensure proper chronological order
      const sortedData = data.sort((a, b) => new Date(a[0]) - new Date(b[0]));

      // Remove duplicates based on timestamps
      const removeDuplicates = data => {
        return data.filter(
          (item, index, self) => index === self.findIndex(t => t[0] === item[0])
        );
      };

      const uniqueData = removeDuplicates(sortedData);
      setMachineCurrent(uniqueData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false); // Set loading to false after fetching
    }
  };

  const updateBucket = bucketName => {
    setCurrentBucket(bucketName);
  };

  const getIntervalTime = () => {
    return 60 * 1000;
  };

  useEffect(() => {
    fetchData();
    const intervalTime = getIntervalTime();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, intervalTime) : null;
    return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [currentBucket, startTime, endTime]);

  // Extract values for dynamic min and max calculations
  const values = machineCurrent.map(item => item[1]);
  const dynamicMin = Math.min(...values) / 1.15; // Dynamic minimum value
  const dynamicMax = Math.max(...values) * 1.15; // Dynamic maximum value

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },

    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: value => dayjs(value).format('HH:mm'),
        interval: 'auto',
        margin: 10
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      min: dynamicMin,
      max: dynamicMax,
      axisLabel: {
        formatter: '{value} A'
      },
      axisPointer: {
        snap: true
      },
      splitLine: {
        show: false
      }
    },
    series: [
      {
        name: `Electricity ${machineName}`,
        type: 'line',
        smooth: true,
        data: machineCurrent,
        markArea: {
          itemStyle: {
            color: 'rgba(255, 173, 177, 0.4)'
          },
          data: [
            [{ name: 'Morning Peak', xAxis: '07:30' }, { xAxis: '10:00' }],
            [{ name: 'Evening Peak', xAxis: '17:30' }, { xAxis: '21:15' }]
          ]
        }
      }
    ]
  };

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>{title}</div>
          {dynamicBucket && (
            <BucketRangeButtons
              current={currentBucket}
              onChange={updateBucket}
            />
          )}
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={option}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  },
};

export default memo(MachineAmpsGraph);
